import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/search', label: '搜索' },
  { to: '/about', label: '关于' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const lastScroll = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      if (current > 120 && current > lastScroll.current) {
        setHidden(true);
        setCategoryOpen(false);
        setSearchOpen(false);
      } else {
        setHidden(false);
      }
      lastScroll.current = current;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    setSearchOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-transform duration-300 ${
          hidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
          <div className="flex h-16 w-full items-center gap-3 px-4 md:h-[72px] md:px-8">
            <Link to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setMenuOpen(false)}>
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-700">
                <span className="font-heading text-lg font-semibold text-accent-300">无</span>
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-[15px] font-semibold tracking-wide text-foreground-950 md:text-base">
                  无码中文字幕
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-foreground-500">
                  Japan · Korea Self-shot
                </span>
              </span>
            </Link>

            <nav className="ml-6 hidden items-center gap-1 lg:flex">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex h-9 items-center rounded-md px-3.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div
                className="relative"
                onMouseEnter={() => setCategoryOpen(true)}
                onMouseLeave={() => setCategoryOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setCategoryOpen((prev) => !prev)}
                  className="flex h-9 cursor-pointer items-center gap-1.5 rounded-md px-3.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  分类
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {categoryOpen && (
                  <div className="absolute left-0 top-full w-[420px] pt-2">
                    <div className="rounded-lg border border-background-200 bg-background-50 p-3 shadow-none">
                      <div className="grid grid-cols-2 gap-1">
                        {categories.map((category) => (
                          <Link
                            key={category.slug}
                            to={`/category/${encodeURIComponent(category.slug)}`}
                            onClick={() => setCategoryOpen(false)}
                            className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                          >
                            <span className="flex h-6 w-6 items-center justify-center rounded bg-background-100 text-primary-600">
                              <i className={`${category.icon} text-sm`} />
                            </span>
                            {category.name}
                          </Link>
                        ))}
                      </div>
                      <div className="mt-2 border-t border-background-200 pt-2">
                        <Link
                          to="/search"
                          onClick={() => setCategoryOpen(false)}
                          className="flex items-center justify-between rounded-md px-2.5 py-2 text-xs text-foreground-500 transition-colors hover:bg-background-100"
                        >
                          浏览全部内容
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-arrow-right-line" />
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            <div className="ml-auto flex items-center gap-2">
              <form onSubmit={submitSearch} className="hidden items-center md:flex">
                <div className="flex h-9 w-[220px] items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 transition-colors focus-within:border-primary-300 focus-within:bg-background-50 lg:w-[280px]">
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-sm" />
                  </span>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索标题、题材或标签"
                    className="h-full w-full border-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                  <button
                    type="submit"
                    className="flex h-6 cursor-pointer items-center rounded-full bg-primary-600 px-2 text-[11px] text-background-50 transition-colors hover:bg-primary-700"
                  >
                    搜索
                  </button>
                </div>
              </form>

              <button
                type="button"
                aria-label="打开搜索"
                onClick={() => setSearchOpen((prev) => !prev)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
              >
                <i className="ri-search-line text-lg" />
              </button>
              <button
                type="button"
                aria-label="打开菜单"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200 text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
              >
                <i className={menuOpen ? 'ri-close-line text-lg' : 'ri-menu-3-line text-lg'} />
              </button>
            </div>
          </div>

          {searchOpen && (
            <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
              <form onSubmit={submitSearch} className="flex h-10 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索标题、题材或标签"
                  className="h-full w-full border-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button type="submit" className="flex h-7 shrink-0 cursor-pointer items-center rounded-full bg-primary-600 px-3 text-xs whitespace-nowrap text-background-50">
                  搜索
                </button>
              </form>
            </div>
          )}

          {menuOpen && (
            <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
              <div className="flex flex-col gap-1">
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-11 items-center justify-between rounded-md px-3 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  首页
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </Link>
                <Link
                  to="/search"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-11 items-center justify-between rounded-md px-3 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  搜索
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-11 items-center justify-between rounded-md px-3 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  关于我们
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </Link>
              </div>
              <div className="mt-4 border-t border-background-200 pt-4">
                <p className="mb-2 px-1 text-[11px] uppercase tracking-[0.2em] text-foreground-400">内容分类</p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${encodeURIComponent(category.slug)}`}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-2.5 py-2.5 text-[13px] text-foreground-800"
                    >
                      <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                        <i className={`${category.icon} text-sm`} />
                      </span>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}